import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../core/api.service';

interface DashboardIndicador {
  titulo: string;
  valor: string;
  detalle: string;
  color: string;
}

interface DashboardResumen {
  nombreEmpresa: string;
  fechaDesde: string;
  fechaHasta: string;
  indicadores: DashboardIndicador[];
  operacion: Record<string, number>;
  tendenciaMensual: unknown[];
  inspeccionesPorArea: DashboardDistribucion[];
  fallasPorEstado: DashboardDistribucion[];
  pqrsPorEstado: DashboardDistribucion[];
}

interface DashboardDistribucion {
  nombre: string;
  total: number;
  porcentaje: number;
  color?: string;
}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <section class="page">
      <div class="title-row">
        <div>
          <p>Panel de control</p>
          <h1>Dashboard</h1>
        </div>
        <div class="actions">
          <button class="btn ghost" (click)="setPreset(7)">7 dias</button>
          <button class="btn ghost" (click)="setPreset(30)">30 dias</button>
          <button class="btn ghost" (click)="setMonth()">Mes</button>
          <button class="btn" (click)="load()">Actualizar</button>
        </div>
      </div>

      <form class="filters" (ngSubmit)="load()">
        <label class="field">
          <span>Desde</span>
          <input name="fechaDesde" type="date" [(ngModel)]="fechaDesde">
        </label>
        <label class="field">
          <span>Hasta</span>
          <input name="fechaHasta" type="date" [(ngModel)]="fechaHasta">
        </label>
        <button class="btn">Aplicar rango</button>
      </form>

      @if (error()) {
        <p class="error">{{ error() }}</p>
      }

      @if (summary(); as data) {
        <div class="period">
          <strong>{{ data.nombreEmpresa }}</strong>
          <span>{{ data.fechaDesde | slice:0:10 }} - {{ data.fechaHasta | slice:0:10 }}</span>
        </div>

        <div class="cards">
          @for (item of data.indicadores; track item.titulo) {
            <article class="card" [style.borderTopColor]="item.color">
              <span>{{ item.titulo }}</span>
              <strong>{{ item.valor }}</strong>
              <small>{{ item.detalle }}</small>
            </article>
          }
        </div>

        <div class="chart-grid">
          <article class="panel">
            <h2>Inspecciones por area</h2>
            <div class="donut-layout">
              <div class="donut" [style.background]="donutBackground(data.inspeccionesPorArea)"></div>
              <div class="legend">
                @for (item of withColors(data.inspeccionesPorArea); track item.nombre) {
                  <div>
                    <span class="swatch" [style.background]="item.color"></span>
                    <strong>{{ item.nombre }}</strong>
                    <em>{{ item.total }} · {{ item.porcentaje }}%</em>
                  </div>
                } @empty {
                  <p>Sin datos en el rango.</p>
                }
              </div>
            </div>
          </article>

          <article class="panel">
            <h2>Fallas por estado</h2>
            <div class="donut-layout compact">
              <div class="donut" [style.background]="donutBackground(data.fallasPorEstado)"></div>
              <div class="legend">
                @for (item of withColors(data.fallasPorEstado); track item.nombre) {
                  <div>
                    <span class="swatch" [style.background]="item.color"></span>
                    <strong>{{ item.nombre }}</strong>
                    <em>{{ item.total }} · {{ item.porcentaje }}%</em>
                  </div>
                } @empty {
                  <p>Sin datos en el rango.</p>
                }
              </div>
            </div>
          </article>

          <article class="panel">
            <h2>PQRS por estado</h2>
            <div class="donut-layout compact">
              <div class="donut" [style.background]="donutBackground(data.pqrsPorEstado)"></div>
              <div class="legend">
                @for (item of withColors(data.pqrsPorEstado); track item.nombre) {
                  <div>
                    <span class="swatch" [style.background]="item.color"></span>
                    <strong>{{ item.nombre }}</strong>
                    <em>{{ item.total }} · {{ item.porcentaje }}%</em>
                  </div>
                } @empty {
                  <p>Sin datos en el rango.</p>
                }
              </div>
            </div>
          </article>
        </div>

        <div class="grid">
          <article class="panel">
            <h2>Operacion</h2>
            <dl>
              @for (item of operationEntries(data.operacion); track item[0]) {
                <div>
                  <dt>{{ label(item[0]) }}</dt>
                  <dd>{{ item[1] }}</dd>
                </div>
              }
            </dl>
          </article>

          <article class="panel">
            <h2>Tendencia mensual</h2>
            <div class="bars">
              @for (item of data.tendenciaMensual; track item['periodo']) {
                <div class="bar-row">
                  <span>{{ item['periodo'] }}</span>
                  <div>
                    <i [style.width.%]="barWidth(item)"></i>
                  </div>
                </div>
              }
            </div>
          </article>
        </div>
      } @else {
        <p>Cargando dashboard...</p>
      }
    </section>
  `,
  styles: [`
    .page {
      display: grid;
      gap: 18px;
    }

    .title-row,
    .actions {
      display: flex;
      gap: 16px;
      align-items: center;
    }

    .title-row {
      justify-content: space-between;
    }

    .actions {
      flex-wrap: wrap;
      justify-content: flex-end;
    }

    .filters {
      display: grid;
      grid-template-columns: repeat(2, minmax(180px, 240px)) auto;
      gap: 12px;
      align-items: end;
      padding: 14px;
      background: white;
      border: 1px solid var(--line);
      border-radius: 8px;
    }

    p,
    h1 {
      margin: 0;
    }

    .title-row p,
    .period span {
      color: var(--muted);
    }

    .period {
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
    }

    .cards {
      display: grid;
      grid-template-columns: repeat(4, minmax(160px, 1fr));
      gap: 14px;
    }

    .card,
    .panel {
      background: white;
      border: 1px solid var(--line);
      border-radius: 8px;
    }

    .card {
      display: grid;
      gap: 6px;
      padding: 18px;
      border-top: 4px solid var(--brand);
    }

    .card strong {
      font-size: 28px;
    }

    .card span,
    .card small {
      color: var(--muted);
    }

    .grid {
      display: grid;
      grid-template-columns: minmax(0, 1.3fr) minmax(280px, .7fr);
      gap: 14px;
    }

    .chart-grid {
      display: grid;
      grid-template-columns: minmax(320px, 1.2fr) repeat(2, minmax(260px, .9fr));
      gap: 14px;
    }

    .panel {
      padding: 18px;
    }

    .panel h2 {
      margin: 0 0 14px;
      font-size: 18px;
    }

    .donut-layout {
      display: grid;
      grid-template-columns: 190px minmax(0, 1fr);
      gap: 18px;
      align-items: center;
    }

    .donut-layout.compact {
      grid-template-columns: 150px minmax(0, 1fr);
    }

    .donut {
      position: relative;
      width: 100%;
      aspect-ratio: 1;
      border-radius: 50%;
      background: #e8edf5;
      box-shadow: inset 0 0 0 1px rgba(23, 32, 51, .08);
    }

    .donut::after {
      content: '';
      position: absolute;
      inset: 22%;
      border-radius: 50%;
      background: white;
      box-shadow: 0 0 0 1px var(--line);
    }

    .legend {
      display: grid;
      gap: 9px;
      min-width: 0;
    }

    .legend div {
      display: grid;
      grid-template-columns: 12px minmax(0, 1fr) auto;
      gap: 8px;
      align-items: center;
      min-width: 0;
      font-size: 13px;
    }

    .legend strong {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .legend em {
      color: var(--muted);
      font-style: normal;
      white-space: nowrap;
    }

    .swatch {
      width: 12px;
      height: 12px;
      border-radius: 3px;
    }

    dl {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 10px;
      margin: 0;
    }

    dl div {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      padding: 10px;
      border: 1px solid var(--line);
      border-radius: 6px;
    }

    dt {
      color: var(--muted);
    }

    dd {
      margin: 0;
      font-weight: 800;
    }

    .ghost {
      background: #eef2f7;
      color: var(--ink);
    }

    .bars {
      display: grid;
      gap: 11px;
    }

    .bar-row {
      display: grid;
      grid-template-columns: 70px minmax(0, 1fr);
      gap: 10px;
      align-items: center;
      font-size: 13px;
      color: var(--muted);
    }

    .bar-row div {
      height: 12px;
      overflow: hidden;
      border-radius: 999px;
      background: #eef2f7;
    }

    .bar-row i {
      display: block;
      height: 100%;
      min-width: 3px;
      border-radius: inherit;
      background: var(--brand);
    }

    .error {
      color: var(--bad);
      font-weight: 700;
    }

    @media (max-width: 1050px) {
      .cards,
      .grid,
      .chart-grid,
      .filters {
        grid-template-columns: 1fr;
      }

      .donut-layout,
      .donut-layout.compact {
        grid-template-columns: minmax(140px, 180px) minmax(0, 1fr);
      }
    }
  `]
})
export class DashboardComponent implements OnInit {
  readonly summary = signal<DashboardResumen | null>(null);
  readonly error = signal('');
  fechaDesde = '';
  fechaHasta = '';
  private readonly palette = ['#246bfe', '#12a594', '#f59e0b', '#dc2626', '#7c3aed', '#0891b2', '#64748b', '#84cc16'];

  constructor(private readonly api: ApiService) {}

  ngOnInit(): void {
    this.setPreset(30, false);
    this.load();
  }

  load(): void {
    this.error.set('');
    this.api.getDashboard({
      fechaDesde: this.fechaDesde,
      fechaHasta: this.fechaHasta
    }).subscribe({
      next: (data) => this.summary.set(data as DashboardResumen),
      error: (err) => this.error.set(err.message ?? 'No se pudo cargar el dashboard.')
    });
  }

  setPreset(days: number, reload = true): void {
    const hasta = new Date();
    const desde = new Date();
    desde.setDate(hasta.getDate() - days);
    this.fechaDesde = this.toDateInput(desde);
    this.fechaHasta = this.toDateInput(hasta);
    if (reload) {
      this.load();
    }
  }

  setMonth(): void {
    const now = new Date();
    this.fechaDesde = this.toDateInput(new Date(now.getFullYear(), now.getMonth(), 1));
    this.fechaHasta = this.toDateInput(now);
    this.load();
  }

  operationEntries(value: Record<string, number>): [string, number][] {
    return Object.entries(value ?? {});
  }

  label(value: string): string {
    return value.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
  }

  withColors(items: DashboardDistribucion[]): DashboardDistribucion[] {
    return (items ?? [])
      .filter((item) => item.total > 0)
      .map((item, index) => ({
        ...item,
        color: this.palette[index % this.palette.length]
      }));
  }

  donutBackground(items: DashboardDistribucion[]): string {
    const values = this.withColors(items);
    const total = values.reduce((sum, item) => sum + item.total, 0);
    if (total <= 0) {
      return '#e8edf5';
    }

    let start = 0;
    const stops = values.map((item) => {
      const end = start + (item.total * 100 / total);
      const segment = `${item.color} ${start}% ${end}%`;
      start = end;
      return segment;
    });

    return `conic-gradient(${stops.join(', ')})`;
  }

  barWidth(item: unknown): number {
    const row = item as Record<string, number>;
    const total = (row['inspecciones'] ?? 0)
      + (row['mantenimientos'] ?? 0)
      + (row['fallas'] ?? 0)
      + (row['pqrs'] ?? 0)
      + (row['checklists'] ?? 0);
    const max = Math.max(...(this.summary()?.tendenciaMensual ?? []).map((entry) => {
      const record = entry as Record<string, number>;
      return (record['inspecciones'] ?? 0)
        + (record['mantenimientos'] ?? 0)
        + (record['fallas'] ?? 0)
        + (record['pqrs'] ?? 0)
        + (record['checklists'] ?? 0);
    }), 1);
    return Math.max(4, Math.round(total * 100 / max));
  }

  private toDateInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
